import React from 'react';
import { Box, Flex, Heading, IconButton } from 'pounceV2';
import { useTheme } from '@mui/material';
import useAIPanelV3 from 'Hooks/useAIPanelV3';
import { AI_PANEL_WIDTH, AI_PANEL_ANIMATION_DURATION_MS } from './constants';
import AIPanelToggleTabV3 from './AIPanelToggleTabV3';

/**
 * AI Panel shell component — the outer container with header (title + close button),
 * a portal target div for page-specific content, and animated width transition.
 *
 * The toggle tab is absolutely positioned to the left of the panel so it
 * never affects layout flow on any page.
 */
const AIPanelV3: React.FC = () => {
  const { isOpen, title, close, setPortalContainer } = useAIPanelV3();
  const {
    palette: { tokens },
  } = useTheme();

  return (
    /* Outer wrapper: overflow visible so the absolutely-positioned toggle tab
        can extend left beyond the panel bounds. The inner clip container (below)
        handles hiding panel content when closed — it inherits width: 100% from
        this box, so when the outer animates to width: 0, the inner also becomes
        0 and clips its children via overflow: hidden. */
    <Box
      data-testid="ai-panel-outer"
      sx={{
        position: 'relative',
        width: isOpen ? AI_PANEL_WIDTH : 0,
        minWidth: isOpen ? AI_PANEL_WIDTH : 0,
        transition: `width ${AI_PANEL_ANIMATION_DURATION_MS}ms ease-in-out, min-width ${AI_PANEL_ANIMATION_DURATION_MS}ms ease-in-out`,
        overflow: 'visible',
        height: '100%',
        flexShrink: 0,
      }}
    >
      <AIPanelToggleTabV3 />

      {/* Inner clip container — hides panel content when outer width is 0 */}
      <Box
        sx={{
          width: '100%',
          height: '100%',
          overflow: 'hidden',
        }}
      >
        <Flex
          data-testid="ai-panel-inner"
          sx={{
            width: AI_PANEL_WIDTH,
            height: '100%',
            flexDirection: 'column',
            borderLeft: '1px solid',
            borderLeftColor: tokens.stroke.neutral[3],
            backgroundColor: tokens.background.neutral.canvas.base,
          }}
        >
          {/* Header bar */}
          <Flex
            data-testid="ai-panel-header"
            align="center"
            justify="space-between"
            p={4}
            sx={{
              borderBottom: '1px solid',
              borderBottomColor: tokens.stroke.neutral[3],
              flexShrink: 0,
            }}
          >
            <Heading variant="h5" fontWeight="bold">
              {title}
            </Heading>
            <IconButton
              icon="close"
              variant="ghost"
              size="small"
              aria-label="Close AI panel"
              onClick={close}
            />
          </Flex>

          {/* Portal target - pages inject content here via createPortal */}
          <Box
            ref={setPortalContainer}
            data-testid="ai-panel-portal-target"
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              pb: 2,
            }}
          />
        </Flex>
      </Box>
    </Box>
  );
};

export default React.memo(AIPanelV3);
